import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Ciclo,
  createCiclo,
  CreateCicloPayload,
  getCiclos,
  getTurnos,
  Turno,
  updateCiclo,
} from '../services/horarios.service';
import { getErrorMessage } from '../utils/errorHandler';

const weekDays: Array<{ value: number; label: string }> = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miercoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sabado' },
  { value: 7, label: 'Domingo' },
];

const CiclosPage: React.FC = () => {
  const [turnos, setTurnos] = useState<Turno[]>([]);
  const [ciclos, setCiclos] = useState<Ciclo[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [nombre, setNombre] = useState('');
  const [turnoId, setTurnoId] = useState('');
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [activo, setActivo] = useState(true);
  const [editId, setEditId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [turnosResp, ciclosResp] = await Promise.all([getTurnos(), getCiclos()]);
      const turnosData = Array.isArray(turnosResp.data) ? turnosResp.data : [];
      const turnosNoRotativos = turnosData.filter((turno) => !turno.rotativo);
      setTurnos(turnosNoRotativos);
      setCiclos(Array.isArray(ciclosResp.data) ? ciclosResp.data : []);
      setTurnoId((prev) => (prev || (turnosNoRotativos[0]?._id ?? '')));
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const turnosById = useMemo(() => {
    const map = new Map<string, Turno>();
    turnos.forEach((turno) => map.set(turno._id, turno));
    return map;
  }, [turnos]);

  const resetForm = () => {
    setNombre('');
    setSelectedDays([]);
    setActivo(true);
    setEditId(null);
    setError('');
  };

  const onToggleDay = (day: number) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort((a, b) => a - b),
    );
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!turnoId) {
      setError('Seleccione un turno');
      return;
    }
    if (!nombre.trim()) {
      setError('Ingrese nombre de ciclo');
      return;
    }
    if (selectedDays.length === 0) {
      setError('Seleccione al menos un dia');
      return;
    }

    const payload: CreateCicloPayload = {
      nombre: nombre.trim(),
      activo,
      bloques: selectedDays.map((diaSemana) => ({ diaSemana, turnoId })),
    };

    setSaving(true);
    try {
      if (editId) {
        await updateCiclo(editId, payload);
        setSuccess('Ciclo actualizado correctamente');
      } else {
        await createCiclo(payload);
        setSuccess('Ciclo creado correctamente');
      }
      resetForm();
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const onEdit = (ciclo: Ciclo) => {
    setEditId(ciclo._id);
    setNombre(ciclo.nombre);
    setActivo(ciclo.activo);

    const firstTurnoId = ciclo.bloques[0]?.turnoId;
    if (typeof firstTurnoId === 'string') {
      setTurnoId(firstTurnoId);
    } else if (firstTurnoId && typeof firstTurnoId === 'object' && '_id' in firstTurnoId) {
      setTurnoId(String(firstTurnoId._id));
    }

    setSelectedDays(ciclo.bloques.map((b) => b.diaSemana).sort((a, b) => a - b));
  };

  const formatBloques = (ciclo: Ciclo): string => {
    return ciclo.bloques
      .slice()
      .sort((a, b) => a.diaSemana - b.diaSemana)
      .map((bloque) => {
        const turnoRef = bloque.turnoId;
        const turno =
          typeof turnoRef === 'string'
            ? turnosById.get(turnoRef)
            : ((turnoRef as Turno | undefined) ?? undefined);
        const day = weekDays.find((d) => d.value === bloque.diaSemana)?.label || `Dia ${bloque.diaSemana}`;
        const horario = turno ? `${turno.horaEntrada}-${turno.horaSalida}` : 'Turno';
        return `${day} (${horario})`;
      })
      .join(' | ');
  };

  return (
    <>
      <h1 className="page-title">Turnos - Ciclos</h1>

      <div className="card">
        <h2 className="card-title">{editId ? 'Editar ciclo' : 'Nuevo ciclo'}</h2>
        {error && <div className="error-msg">{error}</div>}
        {success && <div className="success-msg">{success}</div>}

        <form onSubmit={onSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label>Nombre ciclo</label>
              <input value={nombre} onChange={(e) => setNombre(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Turno</label>
              <select value={turnoId} onChange={(e) => setTurnoId(e.target.value)} required>
                <option value="">Seleccionar...</option>
                {turnos.map((turno) => (
                  <option key={turno._id} value={turno._id}>
                    {turno.nombre} ({turno.horaEntrada}-{turno.horaSalida})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: '10px' }}>
            <label>Dias del ciclo</label>
            <div className="actions-cell">
              {weekDays.map((day) => (
                <label key={day.value} style={{ minWidth: '120px' }}>
                  <input
                    type="checkbox"
                    checked={selectedDays.includes(day.value)}
                    onChange={() => onToggleDay(day.value)}
                    style={{ marginRight: '6px' }}
                  />
                  {day.label}
                </label>
              ))}
            </div>
          </div>

          <div className="checkbox-group">
            <input
              id="ciclo-activo"
              type="checkbox"
              checked={activo}
              onChange={(e) => setActivo(e.target.checked)}
            />
            <label htmlFor="ciclo-activo">Activo</label>
          </div>

          <div className="actions-cell">
            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? 'Guardando...' : editId ? 'Guardar cambios' : 'Crear ciclo'}
            </button>
            {editId && (
              <button className="btn btn-outline" type="button" onClick={resetForm}>
                Cancelar edicion
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="card">
        <h2 className="card-title">Listado de ciclos</h2>
        {loading ? (
          <div className="loading">Cargando...</div>
        ) : ciclos.length === 0 ? (
          <div className="empty-state">No hay ciclos registrados.</div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Composicion</th>
                  <th>Activo</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {ciclos.map((ciclo) => (
                  <tr key={ciclo._id}>
                    <td>{ciclo.nombre}</td>
                    <td>{formatBloques(ciclo) || '-'}</td>
                    <td>{ciclo.activo ? 'Si' : 'No'}</td>
                    <td>
                      <button className="btn btn-outline btn-sm" type="button" onClick={() => onEdit(ciclo)}>
                        Editar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default CiclosPage;
